import {useEffect, useState} from 'react';

const CONTAINER_ID = 'hubspot-messages-iframe-container';

const GREETING_HEIGHT = 140;

const useChatPlacement = () => {
  const [mode, setMode] = useState(() =>
    document.getElementById(CONTAINER_ID) ? 'beside' : 'alone'
  );

  useEffect(() => {
    let resizeObserver;
    let measure;
    let currentContainer = null;

    const stopObservingContainer = () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      if (measure) {
        window.removeEventListener('resize', measure);
        measure = null;
      }
      currentContainer = null;
    };

    const startObservingContainer = (container) => {
      currentContainer = container;
      measure = () => {
        const {height} = container.getBoundingClientRect();
        if (height > window.innerHeight / 2) {
          setMode('hidden');
          return;
        }
        setMode(height > GREETING_HEIGHT ? 'beside' : 'above');
      };

      measure();
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(container);
      }
      window.addEventListener('resize', measure);
    };

    // Re-checks which DOM node (if any) is currently the HubSpot container,
    // and re-wires the observers whenever that identity changes — not just
    // on mount. App.jsx tears down and reloads the HubSpot widget on every
    // location switch (clear({resetWidget: true}) + widget.load()), which
    // removes #hubspot-messages-iframe-container and injects a new node
    // asynchronously, on its own timing (not synchronized with this
    // component's renders). Relying on a prop/dependency to "know" when to
    // re-look can race that reset; watching document.body directly reacts
    // to the actual DOM mutation whenever it happens, regardless of timing.
    const sync = () => {
      const found = document.getElementById(CONTAINER_ID);
      if (found === currentContainer) return;

      stopObservingContainer();
      if (found) {
        startObservingContainer(found);
      } else {
        // No HubSpot (not loaded yet, blocked, or mid-reset on a location
        // switch): take the launcher's corner instead of leaving a gap.
        setMode('alone');
      }
    };

    sync();
    const bodyWatcher = new MutationObserver(sync);
    bodyWatcher.observe(document.body, {childList: true, subtree: true});

    return () => {
      bodyWatcher.disconnect();
      stopObservingContainer();
    };
  }, []);

  return mode;
};

export default useChatPlacement;
